import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Publish as PublishIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import RutaSearch from '../../components/RutaSearch';
import ListaPrecioSearch from '../../components/ListaPrecioSearch';
import ProductoSearch from '../../components/ProductoSearch';
import VendedorSelect from '../../components/VendedorSelect';
import AtributoSearch from '../../components/AtributoSearch';
import descuentoService, {
  CreateDescuentoDto,
  DescuentoDto,
  DescuentoKey,
  ESTADO_DESCUENTO_LABEL,
  TipoDatoCliente,
  descuentoKeyId,
  hoyIso,
  normalizarEstadoDescuento,
  fromEditSearch,
  fromSearchParams,
  toCreatePayload,
  validarDimensionesRegla,
} from '../../services/descuentoService';
import { catalogosService } from '../../services/catalogosService';
import productoService from '../../services/productoService';
import clienteService from '../../services/clienteService';
import rutaService from '../../services/rutaService';
import listaPrecioService from '../../services/listaPrecioService';
import { Clase, Linea, Ramo } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';
import { ListaPrecio } from '../../services/listaPrecioService';
import { Ruta } from '../../services/rutaService';

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };
const seccionSx = { border: '1px solid #e0e0e0', borderRadius: 2, p: { xs: 2, sm: 2.5 }, height: '100%' };

const pagedItems = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  const o = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const items = o.items ?? o.Items;
  return Array.isArray(items) ? (items as T[]) : [];
};

const catalogStr = (item: unknown, camel: string, pascal: string): string => {
  const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
  return String(o[camel] ?? o[pascal] ?? '').trim();
};

const emptyForm = (): CreateDescuentoDto => ({
  codigoLista: '',
  nroCliente: 0,
  codigoRuta: 0,
  nroVendedor: 0,
  atributo1Cli: '',
  atributo2Cli: '',
  codigoArticulo: '',
  codigoLinea: '',
  codigoRamo: '',
  codigoClase: '',
  atributo1Sto: '',
  atributo2Sto: '',
  porcDesc: 0,
  fechaVigencia: null,
  fechaDesde: hoyIso(),
  estado: 'P',
  fechaAprobacion: null,
});

const dtoToForm = (d: DescuentoDto): CreateDescuentoDto => ({
  ...emptyForm(),
  codigoLista: d.codigoLista ?? '',
  nroCliente: Number(d.nroCliente) || 0,
  codigoRuta: Number(d.codigoRuta) || 0,
  nroVendedor: Number(d.nroVendedor) || 0,
  atributo1Cli: d.atributo1Cli ?? '',
  atributo2Cli: d.atributo2Cli ?? '',
  codigoArticulo: d.codigoArticulo ?? '',
  codigoLinea: d.codigoLinea ?? '',
  codigoRamo: d.codigoRamo ?? '',
  codigoClase: d.codigoClase ?? '',
  atributo1Sto: d.atributo1Sto ?? '',
  atributo2Sto: d.atributo2Sto ?? '',
  porcDesc: Number(d.porcDesc) || 0,
  fechaVigencia: d.fechaVigencia ?? null,
  fechaDesde: d.fechaDesde ?? null,
  estado: normalizarEstadoDescuento(d.estado),
  fechaAprobacion: d.fechaAprobacion ?? null,
});

const ESTADO_COLOR = { P: 'warning', A: 'success', I: 'default' } as const;

const formatFechaCorta = (value?: string | null): string => {
  const m = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

const labelsBasicos = (d: Pick<CreateDescuentoDto, 'nroCliente' | 'codigoRuta' | 'codigoLista' | 'codigoArticulo'>) => ({
  cliente: Number(d.nroCliente) > 0 ? `Cliente ${d.nroCliente}` : '',
  ruta: Number(d.codigoRuta) > 0 ? `Ruta ${d.codigoRuta}` : '',
  lista: (d.codigoLista ?? '').trim(),
  articulo: (d.codigoArticulo ?? '').trim(),
});

const dtoTieneDatos = (d: DescuentoDto): boolean =>
  descuentoKeyId(d) !== descuentoKeyId(emptyForm()) ||
  Number(d.porcDesc) !== 0 ||
  Boolean(String(d.fechaVigencia ?? '').trim());

async function enrichLabels(d: CreateDescuentoDto): Promise<ReturnType<typeof labelsBasicos>> {
  const base = labelsBasicos(d);
  const [cliente, ruta, lista, articulo] = await Promise.all([
    Number(d.nroCliente) > 0
      ? clienteService
          .getCliente(Number(d.nroCliente))
          .then((c) => `${c.nroCliente} - ${c.nombre || c.razonSocial || ''}`.trim())
          .catch(() => base.cliente)
      : Promise.resolve(''),
    Number(d.codigoRuta) > 0
      ? rutaService
          .getRuta(Number(d.codigoRuta))
          .then((r) => `${r.codigoRuta}${r.nombreRuta ? ` - ${r.nombreRuta}` : ''}`)
          .catch(() => base.ruta)
      : Promise.resolve(''),
    (d.codigoLista ?? '').trim()
      ? listaPrecioService
          .getListaPrecio((d.codigoLista ?? '').trim())
          .then((l) => {
            const codigo = (l.codigoLista ?? d.codigoLista ?? '').trim();
            return codigo ? `${codigo}${l.descripcion ? ` - ${l.descripcion}` : ''}` : base.lista;
          })
          .catch(() => base.lista)
      : Promise.resolve(''),
    (d.codigoArticulo ?? '').trim()
      ? productoService
          .getProducto((d.codigoArticulo ?? '').trim())
          .then((p) => {
            const codigo = String(p.codigoArticulo ?? d.codigoArticulo ?? '').trim();
            const desc = String(p.descripcion ?? '').trim();
            return codigo ? `${codigo}${desc ? ` - ${desc}` : ''}` : base.articulo;
          })
          .catch(() => base.articulo)
      : Promise.resolve(''),
  ]);
  return { cliente, ruta, lista, articulo };
}

const tieneAtributosCliente = (d: Pick<CreateDescuentoDto, 'atributo1Cli' | 'atributo2Cli'>): boolean =>
  [d.atributo1Cli, d.atributo2Cli].some((v) => Boolean((v ?? '').trim()));

const tieneAtributosArticulo = (d: Pick<CreateDescuentoDto, 'atributo1Sto' | 'atributo2Sto'>): boolean =>
  [d.atributo1Sto, d.atributo2Sto].some((v) => Boolean((v ?? '').trim()));

const tipoDatoClienteDe = (d: CreateDescuentoDto): TipoDatoCliente => {
  if (Number(d.nroCliente) > 0) return 'cliente';
  if (Number(d.codigoRuta) > 0) return 'ruta';
  if (Number(d.nroVendedor) > 0) return 'vendedor';
  if (tieneAtributosCliente(d)) return 'atributo';
  return 'cliente';
};

type TipoDatoArticulo = 'articulo' | 'clasificacion' | 'atributo';

const tieneClasificacion = (d: Pick<CreateDescuentoDto, 'codigoRamo' | 'codigoLinea' | 'codigoClase'>): boolean =>
  [d.codigoRamo, d.codigoLinea, d.codigoClase].some((v) => Boolean((v ?? '').trim()));

const tipoDatoArticuloDe = (d: CreateDescuentoDto): TipoDatoArticulo => {
  if ((d.codigoArticulo ?? '').trim()) return 'articulo';
  if (tieneClasificacion(d)) return 'clasificacion';
  if (tieneAtributosArticulo(d)) return 'atributo';
  return 'articulo';
};

const toDateInput = (value?: string | null): string => {
  if (!value) return '';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const LookupField: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  onSearch: () => void;
  onClear: () => void;
}> = ({ label, value, disabled, onSearch, onClear }) => (
  <TextField
    label={label}
    value={value}
    fullWidth
    disabled={disabled}
    InputProps={{
      readOnly: true,
      endAdornment: !disabled ? (
        <InputAdornment position="end">
          {value ? (
            <IconButton size="small" onClick={onClear} edge="end" aria-label={`Limpiar ${label}`}>
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : null}
          <IconButton size="small" onClick={onSearch} edge="end" aria-label={`Buscar ${label}`}>
            <SearchIcon />
          </IconButton>
        </InputAdornment>
      ) : undefined,
    }}
    sx={fieldSx}
  />
);

const DescuentoForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = location.pathname.endsWith('/editar');

  const [formData, setFormData] = useState<CreateDescuentoDto>(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [labels, setLabels] = useState({ cliente: '', ruta: '', lista: '', articulo: '' });
  const [tipoCliente, setTipoCliente] = useState<TipoDatoCliente>('cliente');
  const [tipoArticulo, setTipoArticulo] = useState<TipoDatoArticulo>('articulo');
  /** Regla tal como se leyó: su clave identifica el registro aunque se editen los datos. */
  const [original, setOriginal] = useState<CreateDescuentoDto | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  /** Lista del maestro del cliente elegido: undefined = sin leer, '' = sin lista asignada. */
  const [listaCliente, setListaCliente] = useState<string | undefined>(undefined);

  const [clienteOpen, setClienteOpen] = useState(false);
  const [rutaOpen, setRutaOpen] = useState(false);
  const [listaOpen, setListaOpen] = useState(false);
  const [articuloOpen, setArticuloOpen] = useState(false);

  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [clases, setClases] = useState<Clase[]>([]);

  const loadCatalogos = useCallback(async () => {
    try {
      const [r, l, c] = await Promise.all([
        catalogosService.getRamos(1, 1000),
        catalogosService.getLineas(1, 1000),
        catalogosService.getClases(1, 1000),
      ]);
      setRamos(
        pagedItems<Ramo>(r).map((item) => ({
          ...item,
          codigoRamo: catalogStr(item, 'codigoRamo', 'CodigoRamo'),
          nombreRamo: catalogStr(item, 'nombreRamo', 'NombreRamo'),
        })),
      );
      setLineas(
        pagedItems<Linea>(l).map((item) => ({
          ...item,
          codigoLinea: catalogStr(item, 'codigoLinea', 'CodigoLinea'),
          nombreLinea: catalogStr(item, 'nombreLinea', 'NombreLinea'),
          codigoRamo: catalogStr(item, 'codigoRamo', 'CodigoRamo'),
        })),
      );
      setClases(
        pagedItems<Clase>(c).map((item) => ({
          ...item,
          codigoClase: catalogStr(item, 'codigoClase', 'CodigoClase'),
          nombreClase: catalogStr(item, 'nombreClase', 'NombreClase'),
          codigoRamo: catalogStr(item, 'codigoRamo', 'CodigoRamo'),
          codigoLinea: catalogStr(item, 'codigoLinea', 'CodigoLinea'),
        })),
      );
    } catch {
      setRamos([]);
      setLineas([]);
      setClases([]);
    }
  }, []);

  const loadDescuento = useCallback(async () => {
    if (!isEdit) return;
    const sp = new URLSearchParams(location.search);
    const fromState = (location.state as { descuento?: DescuentoDto } | null)?.descuento;
    const fromQuery = fromEditSearch(sp);
    const key = fromSearchParams(sp);
    const seedDto = fromState ?? (dtoTieneDatos(fromQuery) ? fromQuery : null);
    const seed = seedDto ? dtoToForm(seedDto) : null;

    if (seed) {
      setFormData(seed);
      setOriginal(seed);
      setTipoCliente(tipoDatoClienteDe(seed));
      setTipoArticulo(tipoDatoArticuloDe(seed));
      setLabels(labelsBasicos(seed));
    } else {
      setLoading(true);
    }

    const aplicar = async (d: DescuentoDto) => {
      const form = dtoToForm(d);
      setFormData(form);
      setOriginal(form);
      setTipoCliente(tipoDatoClienteDe(form));
      setTipoArticulo(tipoDatoArticuloDe(form));
      setLabels(labelsBasicos(form));
      try {
        setLabels(await enrichLabels(form));
      } catch {
        /* etiquetas básicas */
      }
    };

    try {
      try {
        const remote = await descuentoService.getByKey(key);
        if (remote && dtoTieneDatos(remote)) {
          await aplicar(remote);
          return;
        }
      } catch {
        /* by-key puede fallar si el backend trata vacío distinto de null */
      }

      try {
        const page = await descuentoService.getDescuentos({ pageNumber: 1, pageSize: 500, ...key });
        const match = (page.items ?? []).find((item) => descuentoKeyId(item) === descuentoKeyId(key));
        if (match) {
          await aplicar(match);
          return;
        }
      } catch {
        /* listado */
      }

      if (seed) {
        try {
          setLabels(await enrichLabels(seed));
        } catch {
          /* ya están las etiquetas básicas */
        }
        return;
      }

      enqueueSnackbar('No se encontró la oferta', { variant: 'error' });
      navigate('/inventario/descuentos');
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, isEdit, location.search, location.state, navigate]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    loadDescuento();
  }, [loadDescuento]);

  useEffect(() => {
    const nro = Number(formData.nroCliente) || 0;
    setListaCliente(undefined);
    if (!nro) return;
    let cancelado = false;
    clienteService
      .getCliente(nro)
      .then((c) => {
        if (!cancelado) setListaCliente(String(c.nroLista ?? '').trim());
      })
      .catch(() => {
        /* el backend valida la lista del cliente al guardar */
      });
    return () => {
      cancelado = true;
    };
  }, [formData.nroCliente]);

  const listaRegla = (formData.codigoLista ?? '').trim();
  /** Solo advierte: el descuento aplica cuando el documento usa esta lista, aunque el cliente tenga otra. */
  const avisoListaCliente = useMemo(() => {
    if (!(Number(formData.nroCliente) > 0) || !listaRegla || listaCliente === undefined) return null;
    if (!listaCliente) {
      return `El cliente no tiene lista de precios asignada. La oferta solo se aplicará en documentos donde se elija la lista ${listaRegla}.`;
    }
    if (listaCliente.toUpperCase() !== listaRegla.toUpperCase()) {
      return `El cliente tiene asignada la lista ${listaCliente}. La oferta solo se aplicará en documentos donde se elija la lista ${listaRegla}.`;
    }
    return null;
  }, [formData.nroCliente, listaCliente, listaRegla]);

  const hoy = hoyIso();
  /** Un descuento activo solo se puede ver o inactivar; pendientes e inactivos se editan completos. */
  const soloLectura = isEdit && normalizarEstadoDescuento(original?.estado) === 'A';
  const claveCambiada = Boolean(isEdit && original && descuentoKeyId(original) !== descuentoKeyId(formData));
  const validaClave = !isEdit || claveCambiada;
  const hayCambios = Boolean(
    isEdit && original && JSON.stringify(toCreatePayload(original)) !== JSON.stringify(toCreatePayload(formData)),
  );

  const desdeForm = String(formData.fechaDesde ?? '').slice(0, 10);
  const hastaForm = String(formData.fechaVigencia ?? '').slice(0, 10);
  const errorFechas = useMemo(() => {
    const desdeAnterior = String(original?.fechaDesde ?? '').slice(0, 10);
    const hastaAnterior = String(original?.fechaVigencia ?? '').slice(0, 10);
    if (!desdeForm) return 'Indicá la fecha desde.';
    if ((!isEdit || desdeForm !== desdeAnterior) && desdeForm < hoy) return 'La fecha desde no puede ser anterior a hoy.';
    if (hastaForm && (!isEdit || hastaForm !== hastaAnterior) && hastaForm < hoy) {
      return 'La fecha de vencimiento no puede ser anterior a hoy.';
    }
    if (hastaForm && hastaForm < desdeForm) return 'La fecha de vencimiento no puede ser anterior a la fecha desde.';
    return null;
  }, [desdeForm, hastaForm, hoy, isEdit, original?.fechaDesde, original?.fechaVigencia]);

  const cambiarTipoCliente = (tipo: TipoDatoCliente | null) => {
    if (!tipo || tipo === tipoCliente) return;
    setTipoCliente(tipo);
    setFormData((p) => ({ ...p, nroCliente: 0, codigoRuta: 0, nroVendedor: 0, atributo1Cli: '', atributo2Cli: '' }));
    setLabels((p) => ({ ...p, cliente: '', ruta: '' }));
  };

  const cambiarTipoArticulo = (tipo: TipoDatoArticulo | null) => {
    if (!tipo || tipo === tipoArticulo) return;
    setTipoArticulo(tipo);
    setFormData((p) => ({
      ...p,
      codigoArticulo: '',
      atributo1Sto: '',
      atributo2Sto: '',
      codigoRamo: '',
      codigoLinea: '',
      codigoClase: '',
    }));
    setLabels((p) => ({ ...p, articulo: '' }));
  };

  /** En edición se muestran todos los datos de artículo cargados (reglas anteriores pueden combinarlos). */
  const mostrarDatoArticulo = (tipo: TipoDatoArticulo): boolean => {
    if (tipoArticulo === tipo) return true;
    if (!isEdit) return false;
    if (tipo === 'clasificacion') return tieneClasificacion(formData);
    if (tipo === 'atributo') return tieneAtributosArticulo(formData);
    return Boolean((formData.codigoArticulo ?? '').trim());
  };

  const setAtributoCliente = (slot: 0 | 1, value: string) =>
    setFormData((p) => ({ ...p, [slot === 0 ? 'atributo1Cli' : 'atributo2Cli']: value }));

  const setAtributoArticulo = (slot: 0 | 1, value: string) =>
    setFormData((p) => ({ ...p, [slot === 0 ? 'atributo1Sto' : 'atributo2Sto']: value }));

  /** En edición se muestran todos los datos de cliente cargados (reglas anteriores pueden tener más de uno). */
  const mostrarDatoCliente = (tipo: TipoDatoCliente): boolean => {
    if (tipoCliente === tipo) return true;
    if (!isEdit) return false;
    if (tipo === 'cliente') return Number(formData.nroCliente) > 0;
    if (tipo === 'ruta') return Number(formData.codigoRuta) > 0;
    if (tipo === 'atributo') return tieneAtributosCliente(formData);
    return Number(formData.nroVendedor) > 0;
  };

  const lineasFiltradas = useMemo(() => {
    const ramo = (formData.codigoRamo ?? '').trim();
    if (!ramo) return lineas;
    return lineas.filter((l) => (l.codigoRamo ?? '').trim() === ramo);
  }, [formData.codigoRamo, lineas]);

  const clasesFiltradas = useMemo(() => {
    const ramo = (formData.codigoRamo ?? '').trim();
    const linea = (formData.codigoLinea ?? '').trim();
    return clases.filter((c) => {
      if (ramo && (c.codigoRamo ?? '').trim() !== ramo) return false;
      if (linea && (c.codigoLinea ?? '').trim() !== linea) return false;
      return true;
    });
  }, [clases, formData.codigoLinea, formData.codigoRamo]);

  const withCurrentOption = (
    options: { value: string; label: string }[],
    current: string,
  ) => {
    const value = current.trim();
    if (!value || options.some((o) => o.value === value)) return options;
    return [{ value, label: value }, ...options];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const porc = Number(formData.porcDesc);
    if (!Number.isFinite(porc)) {
      enqueueSnackbar('Indicá el porcentaje de descuento', { variant: 'warning' });
      return;
    }
    const error =
      (validaClave ? validarDimensionesRegla(formData) : null) ?? errorFechas;
    if (error) {
      enqueueSnackbar(error, { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const payload = toCreatePayload(formData);
      if (isEdit) {
        await descuentoService.update(payload, original ?? undefined);
        enqueueSnackbar('Oferta guardada exitosamente', { variant: 'success' });
      } else {
        await descuentoService.create(payload);
        enqueueSnackbar('Oferta guardada como pendiente: publicala para que entre en vigencia.', {
          variant: 'success',
        });
      }
      navigate('/inventario/descuentos');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar la oferta'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = async (accion: 'publicar' | 'inactivar') => {
    if (!original) return;
    if (accion === 'inactivar' && !window.confirm('¿Inactivar esta oferta? Deja de aplicarse en los documentos.')) {
      return;
    }
    try {
      setCambiandoEstado(true);
      const clave: DescuentoKey = original;
      const actualizado =
        accion === 'publicar' ? await descuentoService.publicar(clave) : await descuentoService.inactivar(clave);
      const form = dtoToForm(actualizado);
      setFormData(form);
      setOriginal(form);
      enqueueSnackbar(accion === 'publicar' ? 'Oferta publicada: ya está en vigencia.' : 'Oferta inactivada.', {
        variant: 'success',
      });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cambiar el estado de la oferta'), { variant: 'error' });
    } finally {
      setCambiandoEstado(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const disabledKey = soloLectura;
  const estadoOriginal = normalizarEstadoDescuento(original?.estado);
  const desdeOriginal = String(original?.fechaDesde ?? '').slice(0, 10);
  const puedePublicar = isEdit && estadoOriginal !== 'A';
  const publicarBloqueo = !puedePublicar
    ? ''
    : hayCambios
      ? 'Guardá los cambios antes de publicar.'
      : desdeOriginal && desdeOriginal > hoy
        ? `Se podrá publicar a partir del ${formatFechaCorta(desdeOriginal)}.`
        : '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inventario/descuentos')} sx={{ textTransform: 'none' }}>
              Ofertas
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? (soloLectura ? 'Oferta' : 'Modificando una oferta') : 'Nueva oferta'}
            </Typography>
            {isEdit && (
              <Chip label={ESTADO_DESCUENTO_LABEL[estadoOriginal]} color={ESTADO_COLOR[estadoOriginal]} size="small" />
            )}
            {isEdit && original?.fechaAprobacion && (
              <Typography variant="body2" color="text.secondary">
                Publicado el {formatFechaCorta(original.fechaAprobacion)}
              </Typography>
            )}
          </Stack>

          {soloLectura && (
            <Alert severity="info" sx={{ mb: 3 }}>
              La oferta está activa y se está aplicando. Para modificarla, inactivala primero.
            </Alert>
          )}
          {!isEdit && (
            <Alert severity="info" sx={{ mb: 3 }}>
              La oferta se guarda como pendiente y no se aplica hasta que la publiques.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Primero se evalúan la fecha de vigencia y la lista de precios. Después se cruza un dato del cliente (cliente, ruta, vendedor o atributos, uno solo) con un dato del artículo (artículo, clasificación o atributos).
          </Typography>

          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const tag = (e.target as HTMLElement).tagName;
              if (tag !== 'BUTTON' && tag !== 'TEXTAREA') e.preventDefault();
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Vigencia, lista de precios y descuento
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Vigente desde"
                  type="date"
                  required
                  value={toDateInput(formData.fechaDesde)}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      fechaDesde: e.target.value ? e.target.value : null,
                    }))
                  }
                  disabled={soloLectura}
                  fullWidth
                  helperText="Desde ese día se puede publicar. No puede ser anterior a hoy."
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: hoy }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Vigente hasta"
                  type="date"
                  value={toDateInput(formData.fechaVigencia)}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      fechaVigencia: e.target.value ? e.target.value : null,
                    }))
                  }
                  disabled={soloLectura}
                  fullWidth
                  helperText="Válido todo ese día; al siguiente ya no aplica. Vacío = sin fecha límite."
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: desdeForm > hoy ? desdeForm : hoy }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LookupField
                  label="Lista de precios"
                  value={labels.lista}
                  disabled={disabledKey}
                  onSearch={() => setListaOpen(true)}
                  onClear={() => {
                    setFormData((p) => ({ ...p, codigoLista: '' }));
                    setLabels((p) => ({ ...p, lista: '' }));
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.5 }}>
                  Aplica en los documentos donde se elija esta lista. Vacío = cualquier lista.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Porcentaje de descuento"
                  type="number"
                  required
                  value={formData.porcDesc === 0 || formData.porcDesc ? formData.porcDesc : ''}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      porcDesc: e.target.value === '' ? 0 : Number(e.target.value),
                    }))
                  }
                  disabled={soloLectura}
                  fullWidth
                  inputProps={{ step: 0.01, style: { textAlign: 'right', fontWeight: 600 } }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={seccionSx}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Datos del cliente
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    color="primary"
                    value={tipoCliente}
                    onChange={(_, v: TipoDatoCliente | null) => cambiarTipoCliente(v)}
                    disabled={disabledKey}
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="cliente" sx={{ textTransform: 'none' }}>
                      Cliente
                    </ToggleButton>
                    <ToggleButton value="ruta" sx={{ textTransform: 'none' }}>
                      Ruta
                    </ToggleButton>
                    <ToggleButton value="vendedor" sx={{ textTransform: 'none' }}>
                      Vendedor
                    </ToggleButton>
                    <ToggleButton value="atributo" sx={{ textTransform: 'none' }}>
                      Atributo
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Grid container spacing={2}>
                    {mostrarDatoCliente('cliente') && (
                      <Grid item xs={12}>
                        <LookupField
                          label="Cliente"
                          value={labels.cliente}
                          disabled={disabledKey}
                          onSearch={() => setClienteOpen(true)}
                          onClear={() => {
                            setFormData((p) => ({ ...p, nroCliente: 0 }));
                            setLabels((p) => ({ ...p, cliente: '' }));
                          }}
                        />
                        {Number(formData.nroCliente) > 0 && listaCliente !== undefined && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.5 }}>
                            {listaCliente
                              ? `Lista asignada al cliente: ${listaCliente}`
                              : 'El cliente no tiene lista asignada.'}
                          </Typography>
                        )}
                      </Grid>
                    )}
                    {mostrarDatoCliente('ruta') && (
                      <Grid item xs={12}>
                        <LookupField
                          label="Ruta"
                          value={labels.ruta}
                          disabled={disabledKey}
                          onSearch={() => setRutaOpen(true)}
                          onClear={() => {
                            setFormData((p) => ({ ...p, codigoRuta: 0 }));
                            setLabels((p) => ({ ...p, ruta: '' }));
                          }}
                        />
                      </Grid>
                    )}
                    {mostrarDatoCliente('vendedor') && (
                      <Grid item xs={12}>
                        <VendedorSelect
                          label="Vendedor"
                          value={Number(formData.nroVendedor) > 0 ? Number(formData.nroVendedor) : null}
                          onChange={(nro) => setFormData((p) => ({ ...p, nroVendedor: nro ?? 0 }))}
                          allowEmpty
                          emptyOptionLabel="Sin vendedor"
                          disabled={disabledKey}
                          margin="none"
                        />
                      </Grid>
                    )}
                    {mostrarDatoCliente('atributo') && (
                      <Grid item xs={12}>
                        <AtributoSearch
                          origen="cliente"
                          seleccion={[formData.atributo1Cli ?? '', formData.atributo2Cli ?? '']}
                          disabled={disabledKey}
                          onChange={setAtributoCliente}
                        />
                      </Grid>
                    )}
                  </Grid>
                  {!soloLectura && avisoListaCliente && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {avisoListaCliente}
                    </Alert>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={seccionSx}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Datos del artículo
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    color="primary"
                    value={tipoArticulo}
                    onChange={(_, v: TipoDatoArticulo | null) => cambiarTipoArticulo(v)}
                    disabled={disabledKey}
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="articulo" sx={{ textTransform: 'none' }}>
                      Artículo
                    </ToggleButton>
                    <ToggleButton value="clasificacion" sx={{ textTransform: 'none' }}>
                      Clasificación
                    </ToggleButton>
                    <ToggleButton value="atributo" sx={{ textTransform: 'none' }}>
                      Atributo
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Grid container spacing={2}>
                    {mostrarDatoArticulo('articulo') && (
                      <Grid item xs={12}>
                        <LookupField
                          label="Artículo"
                          value={labels.articulo}
                          disabled={disabledKey}
                          onSearch={() => setArticuloOpen(true)}
                          onClear={() => {
                            setFormData((p) => ({ ...p, codigoArticulo: '' }));
                            setLabels((p) => ({ ...p, articulo: '' }));
                          }}
                        />
                      </Grid>
                    )}
                    {mostrarDatoArticulo('atributo') && (
                      <Grid item xs={12}>
                        <AtributoSearch
                          origen="articulo"
                          seleccion={[formData.atributo1Sto ?? '', formData.atributo2Sto ?? '']}
                          disabled={disabledKey}
                          onChange={setAtributoArticulo}
                        />
                      </Grid>
                    )}
                    {mostrarDatoArticulo('clasificacion') && (
                      <>
                        <Grid item xs={12}>
                          <SearchableSelect
                            label="Ramo"
                            allowEmpty
                            emptyOptionLabel="Sin ramo"
                            disabled={disabledKey}
                            value={(formData.codigoRamo || '').trim() || null}
                            onChange={(v) =>
                              setFormData((p) => ({
                                ...p,
                                codigoRamo: (v ?? '').trim(),
                                codigoLinea: '',
                                codigoClase: '',
                              }))
                            }
                            options={withCurrentOption(
                              ramos.map((r) => ({
                                value: (r.codigoRamo ?? '').trim(),
                                label: `${(r.codigoRamo ?? '').trim()} - ${r.nombreRamo}`,
                              })),
                              formData.codigoRamo || '',
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <SearchableSelect
                            label="Línea"
                            allowEmpty
                            emptyOptionLabel="Sin línea"
                            disabled={disabledKey}
                            value={(formData.codigoLinea || '').trim() || null}
                            onChange={(v) => setFormData((p) => ({ ...p, codigoLinea: (v ?? '').trim(), codigoClase: '' }))}
                            options={withCurrentOption(
                              lineasFiltradas.map((l) => ({
                                value: (l.codigoLinea ?? '').trim(),
                                label: `${(l.codigoLinea ?? '').trim()} - ${l.nombreLinea}`,
                              })),
                              formData.codigoLinea || '',
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <SearchableSelect
                            label="Clase"
                            allowEmpty
                            emptyOptionLabel="Sin clase"
                            disabled={disabledKey}
                            value={(formData.codigoClase || '').trim() || null}
                            onChange={(v) => setFormData((p) => ({ ...p, codigoClase: (v ?? '').trim() }))}
                            options={withCurrentOption(
                              clasesFiltradas.map((c) => ({
                                value: (c.codigoClase ?? '').trim(),
                                label: `${(c.codigoClase ?? '').trim()} - ${c.nombreClase}`,
                              })),
                              formData.codigoClase || '',
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }} alignItems={{ sm: 'center' }}>
              {!soloLectura && (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving || cambiandoEstado}
                  sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
                </Button>
              )}
              {puedePublicar && (
                <Tooltip title={publicarBloqueo}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PublishIcon />}
                      onClick={() => cambiarEstado('publicar')}
                      disabled={saving || cambiandoEstado || Boolean(publicarBloqueo)}
                      sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                    >
                      Publicar
                    </Button>
                  </span>
                </Tooltip>
              )}
              {isEdit && estadoOriginal !== 'I' && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<BlockIcon />}
                  onClick={() => cambiarEstado('inactivar')}
                  disabled={saving || cambiandoEstado}
                  sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                >
                  Inactivar
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/descuentos')}
                disabled={saving || cambiandoEstado}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {soloLectura ? 'Volver' : 'ESC-Cancelar'}
              </Button>
            </Stack>
            {publicarBloqueo && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {publicarBloqueo}
              </Typography>
            )}
          </form>
        </CardContent>
      </Card>

      <ClienteSearch
        open={clienteOpen}
        onClose={() => setClienteOpen(false)}
        onSelect={(cliente) => {
          setFormData((p) => ({ ...p, nroCliente: cliente.nroCliente }));
          setLabels((p) => ({ ...p, cliente: `${cliente.nroCliente} - ${cliente.nombre || cliente.razonSocial || ''}` }));
          setClienteOpen(false);
        }}
      />
      <RutaSearch
        open={rutaOpen}
        onClose={() => setRutaOpen(false)}
        onSelect={(ruta: Ruta) => {
          const codigo = Number(ruta.codigoRuta) || 0;
          setFormData((p) => ({ ...p, codigoRuta: codigo }));
          setLabels((p) => ({
            ...p,
            ruta: codigo ? `${codigo}${ruta.nombreRuta ? ` - ${ruta.nombreRuta}` : ''}` : '',
          }));
          setRutaOpen(false);
        }}
      />
      <ListaPrecioSearch
        open={listaOpen}
        onClose={() => setListaOpen(false)}
        onSelect={(lista: ListaPrecio) => {
          const codigo = (lista.codigoLista ?? '').trim();
          setFormData((p) => ({ ...p, codigoLista: codigo }));
          setLabels((p) => ({
            ...p,
            lista: codigo ? `${codigo}${lista.descripcion ? ` - ${lista.descripcion}` : ''}` : '',
          }));
          setListaOpen(false);
        }}
      />

      <ProductoSearch
        open={articuloOpen}
        onClose={() => setArticuloOpen(false)}
        onSelect={(producto) => {
          const codigo = String(producto.codigoArticulo ?? '').trim();
          const desc = String(producto.descripcion ?? '').trim();
          setFormData((p) => ({ ...p, codigoArticulo: codigo }));
          setLabels((p) => ({
            ...p,
            articulo: codigo ? `${codigo}${desc ? ` - ${desc}` : ''}` : '',
          }));
        }}
      />
    </Box>
  );
};

export default DescuentoForm;